import type { ActionReducerMapBuilder, PayloadAction } from "@reduxjs/toolkit";

interface LegacyHandler<S> {
  (state: S, payload: never): S;
}

/**
 * SCRUM-2119. The five slices in that ticket's census (templates, translations,
 * histories, product properties, images) do not share a loading/error pattern:
 * none of them keeps a pending flag, and only translations stores GET_ERRORS.
 * What the census matched was the same legacy-action body, `{ ...state, field:
 * action.payload }`. This helper is that body, once. A case that does something
 * else (filter a list, copy `payload.msg` onto `message`) stays a function.
 */
export function legacyCases<S>(
  builder: ActionReducerMapBuilder<S>,
  cases: Record<string, keyof S | LegacyHandler<S>>
): void {
  for (const [type, handler] of Object.entries(cases)) {
    builder.addCase(type, (draft, action: PayloadAction<never>) => {
      // Immer hands the case a Draft<S>, which for a generic S tsc cannot prove
      // assignable back to S. The legacy bodies only read it and return a fresh
      // object, which Immer takes as the replacement state.
      const state = draft as S;
      if (typeof handler === "function") return handler(state, action.payload);
      return { ...state, [handler]: action.payload };
    });
  }
}
